import { inject, Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Auth, EmailAuthProvider, GoogleAuthProvider, reauthenticateWithCredential, updateEmail, updatePassword, User, UserCredential } from '@angular/fire/auth';
import { applyActionCode, authState, browserLocalPersistence, browserSessionPersistence, confirmPasswordReset, createUserWithEmailAndPassword, idToken, linkWithCredential, sendEmailVerification, sendPasswordResetEmail, signInAnonymously, signInWithEmailAndPassword, signInWithPopup, updateProfile, user, verifyPasswordResetCode } from '@angular/fire/auth';
import { Observable } from 'rxjs';

import { AuthError, AuthErrorCode } from './utils/error';

/** User profile data structure for profile updates */
export type UserProfile = { displayName?: string | null, photoURL?: string | null };

/**
 * Core authentication service providing Firebase Auth integration.
 * Manages user authentication flows, profile updates, and email verification.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthCoreService {

  //#region Dependencies
  /** Firebase Authentication instance */
  protected readonly auth: Auth = inject(Auth);
  //#endregion

  //#region Properties
  /** Current authenticated user */
  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  /** Observable stream of user state changes (sign-in, sign-out, token refresh) */
  readonly user$: Observable<User | null> = user(this.auth);

  /** Observable stream of authentication state changes (sign-in, sign-out only) */
  readonly authState$: Observable<User | null> = authState(this.auth);

  /** Observable stream of ID token changes */
  readonly authToken$: Observable<string | null> = idToken(this.auth);
  //#endregion

  //#region Initialization
  /**
   * Initializes the authentication service.
   * @param languageCode Language code for auth UI (e.g., 'en', 'tr'). Defaults to device language.
   */
  public initialize(languageCode?: string): void {
    languageCode ? this.auth.languageCode = languageCode : this.auth.useDeviceLanguage();
  }
  //#endregion

  //#region Sign Methods
  /**
   * Re-authenticates a user with their email and password.
   * Required for sensitive operations like email or password changes.
   * @param user User to re-authenticate
   * @param password User's current password
   * @throws {AuthError} Re-authentication failure
   */
  public async reauthenticateWithEmail(user: User, password: string): Promise<void> {
    const credential = EmailAuthProvider.credential(user.email!, password);

    try {
      await reauthenticateWithCredential(user, credential);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Signs in a user with email and password.
   * Migrates anonymous user data if applicable.
   * @param email User's email address
   * @param password User's password
   * @param remember If true, persists session across browser restarts
   * @returns Authenticated user
   * @throws {AuthError} Authentication failure
   */
  public async signIn(email: string, password: string, remember: boolean = false): Promise<User> {
    const currentUser = this.auth.currentUser;

    try {
      await this.auth.setPersistence(remember ? browserLocalPersistence : browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);

      if (currentUser && currentUser.isAnonymous) {
        try {
          await this.migrateFromAnonymousUser(currentUser);
        } catch (migrationError) {
          console.error('Failed to migrate anonymous user data:', migrationError);
        }
      }

      return userCredential.user;
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Signs in a user anonymously.
   * Creates a temporary account that can later be upgraded by linking credentials.
   * @returns Anonymous user
   * @throws {AuthError} Sign-in failure
   */
  public async signInAnonymously(): Promise<User> {
    try {
      const userCredential = await signInAnonymously(this.auth);
      return userCredential.user;
    }
    catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Signs in a user with Google authentication via popup.
   * @returns Authenticated user
   * @throws {AuthError} Authentication failure
   */
  public async signInWithGoogle(): Promise<User> {
    const provider = new GoogleAuthProvider();
    try {
      const userCredential = await signInWithPopup(this.auth, provider);
      return userCredential.user;
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Signs out the currently authenticated user.
   * @throws {AuthError} Sign-out failure
   */
  public async signOut(): Promise<void> {
    try {
      await this.auth.signOut();
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Creates a new user account with email and password.
   * Upgrades anonymous account if one exists, otherwise creates new account.
   * Sends email verification after successful registration.
   * @param email User's email address
   * @param password User's password
   * @returns Created or upgraded user
   * @throws {AuthError} Registration failure
   */
  public async signUp(email: string, password: string): Promise<User> {
    const currentUser = this.auth.currentUser;

    try {
      let userCredential: UserCredential;

      if (currentUser && currentUser.isAnonymous) {
        const newCredential = EmailAuthProvider.credential(email, password);
        userCredential = await linkWithCredential(currentUser, newCredential);
      } else {
        userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      }

      await sendEmailVerification(userCredential.user);
      return userCredential.user;
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }
  //#endregion

  //#region User Methods
  /**
   * Migrates user data from anonymous to authenticated account.
   * @param anonymousUser Anonymous user to migrate from
   * @throws {AuthError} Migration failure
   */
  private async migrateFromAnonymousUser(anonymousUser: User): Promise<void> {
    // Migration logic placeholder - implement based on application needs
  }

  /**
   * Updates user email address.
   * @param user User to update
   * @param newEmail New email address
   * @throws {AuthError} Update failure
   */
  public async updateEmail(user: User, newEmail: string): Promise<void> {
    try {
      await updateEmail(user, newEmail);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Updates user password.
   * @param user User to update
   * @param newPassword New password
   * @throws {AuthError} Update failure
   */
  public async updatePassword(user: User, newPassword: string): Promise<void> {
    try {
      await updatePassword(user, newPassword);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Updates user profile information (displayName and/or photoURL).
   * @param user User to update
   * @param profile Profile data to update
   * @throws {AuthError} Update failure
   */
  public async updateProfile(user: User, profile: UserProfile): Promise<void> {
    try {
      await updateProfile(user, profile);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }
  //#endregion

  //#region Confirm Methods
  /**
   * Confirms password reset with token and sets new password.
   * @param token Password reset token from email
   * @param newPassword New password to set
   * @throws {AuthError} Confirmation failure
   */
  public async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, token, newPassword);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }
  //#endregion

  //#region Send Email Methods
  /**
   * Sends email verification to current user.
   * @param continueUrl Optional redirect URL after verification
   * @throws {AuthError} User not found or send failure
   */
  public async sendEmailVerification(continueUrl?: string): Promise<void> {
    const user = this.auth.currentUser;

    if (user === null) {
      throw new AuthError(AuthErrorCode.USER_NOT_FOUND);
    }

    try {
      const actionCodeSettings = continueUrl ? { url: continueUrl } : undefined;
      await sendEmailVerification(user, actionCodeSettings);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Sends password reset email to specified address.
   * @param email Target email address
   * @param continueUrl Optional redirect URL after password reset
   * @throws {AuthError} Send failure
   */
  public async sendPasswordResetEmail(email: string, continueUrl?: string): Promise<void> {
    try {
      const actionCodeSettings = continueUrl ? { url: continueUrl } : undefined;
      await sendPasswordResetEmail(this.auth, email, actionCodeSettings);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }
  //#endregion

  //#region Verification Methods
  /**
   * Verifies user email with verification token.
   * @param token Email verification token from email
   * @throws {AuthError} Invalid or expired token
   */
  public async verifyEmailVerificationToken(token: string): Promise<void> {
    try {
      await applyActionCode(this.auth, token);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }

  /**
   * Verifies password reset token and retrieves associated email.
   * @param token Password reset token to verify
   * @returns Email address associated with the token
   * @throws {AuthError} Invalid or expired token
   */
  public async verifyPasswordResetToken(token: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, token);
    } catch (error) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw error;
    }
  }
  //#endregion
}
